import { Fragment, useState } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import {
  CancelSubmitButtons,
  CheckMarkIcon,
  EditIcon,
  errorToast,
} from "~/common/components";
import Modal from "~/common/components/Modal";
import { Combobox } from "~/common/components/ui/Combobox";
import {
  NOTIFY_LIST_SETTING,
  NOTIFY_LIST_SETTING_OPTIONS,
} from "~/providers/constants/teamMembers";
import { useCurrentTeamMemberContext } from "~/providers/hooks";
import { useUpdateOrganizationNotifyList } from "~/providers/hooks/useUpdateOrganizationNotifyList";
import { notifyListFormSchema } from "~/providers/schemas/teamMembers";
import {
  NotifyListForm,
  OrganizationNotifyListResponse,
  OrganizationProvidersResponse,
} from "~/providers/types/teamMembers";

interface UnassignedConsultationsNotifyListModalProps {
  notifyList: OrganizationNotifyListResponse;
  availableProviders: OrganizationProvidersResponse[];
}

export const UnassignedConsultationsNotifyListModal = ({
  notifyList: { setting, notify_list },
  availableProviders,
}: UnassignedConsultationsNotifyListModalProps) => {
  const [show, setShow] = useState(false);
  const currentTeamMember = useCurrentTeamMemberContext();
  const { mutate: updateNotifyList, isPending } =
    useUpdateOrganizationNotifyList();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NotifyListForm>({
    defaultValues: {
      setting,
      customList:
        setting === "custom" ? notify_list.map((item) => item.id) : undefined,
    },
    resolver: zodResolver(notifyListFormSchema),
  });

  const selectedSetting = watch("setting");

  const stringifiedAdminsList = availableProviders
    .filter((item) => item.is_admin)
    .map((item) => item.full_name)
    .join(", ");

  const providerOptions = availableProviders.map((provider) => ({
    value: provider.id,
    label: provider.full_name,
  }));

  const onClose = () => {
    reset();
    setShow(false);
  };

  const onSubmit: SubmitHandler<NotifyListForm> = ({ setting, customList }) => {
    updateNotifyList(
      {
        organizationId:
          currentTeamMember.organization_team_members[0].organization.id,
        setting,
        organization_team_member_ids:
          setting === "custom" ? customList : undefined,
      },
      {
        onSuccess: () => {
          setShow(false);
        },
        onError: errorToast,
      },
    );
  };

  return (
    <Fragment>
      <button
        className="h-fit w-fit"
        onClick={() => {
          setShow(true);
        }}
      >
        <EditIcon />
      </button>
      <Modal
        title="Edit notify list"
        show={show}
        onClose={onClose}
        className="max-h-[80vh] text-sm"
      >
        <Controller
          control={control}
          name="setting"
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onChange={field.onChange}
              className="flex flex-col gap-3"
            >
              {NOTIFY_LIST_SETTING_OPTIONS.map((option) => (
                <Fragment key={option.value}>
                  <Radio value={option.value}>
                    {({ checked, disabled }) => (
                      <div className="flex cursor-pointer items-center gap-1">
                        <span
                          className={`flex size-5 items-center justify-center rounded-full border-2 ${
                            checked
                              ? "border-primary-500 bg-primary-500 text-neutral-25"
                              : "border-neutral-700 bg-neutral-25 text-transparent"
                          } ${disabled ? "opacity-60" : ""}`}
                        >
                          <CheckMarkIcon />
                        </span>
                        <p>{option.label}</p>
                      </div>
                    )}
                  </Radio>
                  {selectedSetting === NOTIFY_LIST_SETTING.CUSTOM &&
                    option.value === NOTIFY_LIST_SETTING.CUSTOM && (
                      <div className="pl-6">
                        <Controller
                          control={control}
                          name="customList"
                          render={({ field }) => (
                            <Combobox
                              id="notify-list-users"
                              label="Users"
                              placeholder="Search users"
                              multiple
                              options={providerOptions}
                              shouldShowUnassignedOption={false}
                              value={field.value ?? []}
                              onChange={(value) => {
                                field.onChange(value);
                              }}
                            />
                          )}
                        />
                        {errors.customList && (
                          <p className="mt-2 text-xs text-red-500">
                            {errors.customList.message}
                          </p>
                        )}
                      </div>
                    )}
                  {selectedSetting === NOTIFY_LIST_SETTING.ADMINS &&
                    option.value === NOTIFY_LIST_SETTING.ADMINS && (
                      <p className="pl-6">{stringifiedAdminsList}</p>
                    )}
                </Fragment>
              ))}
            </RadioGroup>
          )}
        />
        <CancelSubmitButtons
          containerClassName="justify-end"
          submitClassName="w-fit"
          cancelClassName="w-fit"
          submitButtonLabel="Save changes"
          handleCancel={onClose}
          handleSubmit={handleSubmit(onSubmit)}
          disableCancel={isPending}
          disableSubmit={isPending}
        />
      </Modal>
    </Fragment>
  );
};
